import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { DirectUpload } from "@rails/activestorage";
import { post } from "@rails/request.js";
import { interpolate } from "../lib/interpolate";

export default class extends Controller {
  static targets = [
    "fileInput",
    "emptyState",
    "actionBar",
    "counter",
    "photo",
    "photosContainer",
    "uploadDialog",
    "closeDialogButton",
    "statusContainer",
    "progressBar",
    "progressLabel",
    "errorSummary",
    "retryButton",
    "uploadButton",
    "limitNotice",
    "continueButton",
    // Optional element carrying extra params (as a JSON data-params attribute)
    // to send with every photo create — e.g. the paper whose picker started
    // the upload, so the server can slot the new tile into that picker.
    "context",
  ];

  static values = {
    url: String,
    directUploadUrl: String,
    // Optional cap on the number of photos in the library (0 = unlimited).
    // Extra files are dropped client-side; the server enforces the same cap.
    max: { type: Number, default: 0 },
    // Wording from the view, so uploads report progress in the domain's
    // language: %{...} templates plus the three plural forms of "photo".
    messages: { type: Object, default: {} },
    photoNoun: { type: Object, default: {} },
  };

  connect() {
    this.uploading = false;
    this.queue = [];
    this.failed = [];
    this.done = 0;
    this.total = 0;
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this);
    this.installDropTarget();
    this.updateEmptyState();
    this.autoOpenFromHash();
  }

  disconnect() {
    this.removeDropTarget();
    window.removeEventListener("beforeunload", this.boundBeforeUnload);
  }

  // ---------- opening dialogs ----------

  openFileDialog() {
    if (this.redirectToNewFilterIfNeeded()) return;
    this.fileInputTarget.click();
  }

  openUploadDialog() {
    if (this.redirectToNewFilterIfNeeded()) return;
    this.uploadDialogTarget.showModal();
  }

  // When an "upload" button is clicked while the library is filtered to
  // something other than "Nové", redirect to the default view first (so newly
  // uploaded photos are immediately visible) and auto-reopen on the next page.
  redirectToNewFilterIfNeeded() {
    const url = new URL(window.location.href);
    const filter = url.searchParams.get("filter");
    if (!filter || filter === "new") return false;
    url.searchParams.delete("filter");
    url.hash = "upload-photos";
    Turbo.visit(url.toString(), { action: "advance" });
    return true;
  }

  autoOpenFromHash() {
    if (window.location.hash !== "#upload-photos") return;
    history.replaceState(null, "", window.location.pathname + window.location.search);
    requestAnimationFrame(() => {
      if (this.hasUploadDialogTarget) this.uploadDialogTarget.showModal();
    });
  }

  closeUploadDialog() {
    // Stop queueing further files; the in-flight upload (if any) finishes in the
    // background — DirectUpload XHRs are not aborted here.
    this.queue = [];
    if (this.hasUploadDialogTarget) this.uploadDialogTarget.close();
  }

  // ---------- file selection ----------

  filesSelected(event) {
    this.enqueueFiles(Array.from(event.target.files || []));
    event.target.value = ""; // allow re-selecting the same file later
  }

  enqueueFiles(files) {
    let images = files.filter((f) => f.type.startsWith("image/"));
    if (this.maxValue > 0) {
      const slots = this.maxValue - this.photoTargets.length - this.queue.length - (this.uploading ? 1 : 0);
      images = images.slice(0, Math.max(slots, 0));
    }
    if (images.length === 0) return;
    if (!this.directUploadUrlValue) {
      console.error("Missing direct upload URL for photo upload.");
      return;
    }

    if (!this.uploading) {
      this.done = 0;
      this.failed = [];
      this.total = 0;
    }
    for (const file of images) {
      this.queue.push({ file, name: file.name, size: file.size });
      this.total += 1;
    }
    this.startUpload();
  }

  // ---------- drag & drop (page-wide) ----------

  installDropTarget() {
    this.dropOverlay = document.createElement("div");
    this.dropOverlay.dataset.uploadOverlay = "";
    this.dropOverlay.className =
      "fixed inset-0 z-50 hidden items-center justify-center bg-rose-600/80 " +
      "text-white text-2xl font-semibold pointer-events-none";
    this.dropOverlay.textContent = "Pustite fotky sem";
    document.body.appendChild(this.dropOverlay);

    this.dragDepth = 0;
    this.boundDragEnter = this.handleDragEnter.bind(this);
    this.boundDragOver = (e) => e.preventDefault();
    this.boundDragLeave = this.handleDragLeave.bind(this);
    this.boundDrop = this.handleDrop.bind(this);
    document.addEventListener("dragenter", this.boundDragEnter);
    document.addEventListener("dragover", this.boundDragOver);
    document.addEventListener("dragleave", this.boundDragLeave);
    document.addEventListener("drop", this.boundDrop);
  }

  removeDropTarget() {
    document.removeEventListener("dragenter", this.boundDragEnter);
    document.removeEventListener("dragover", this.boundDragOver);
    document.removeEventListener("dragleave", this.boundDragLeave);
    document.removeEventListener("drop", this.boundDrop);
    this.dropOverlay?.remove();
  }

  handleDragEnter(event) {
    if (!this.eventHasFiles(event)) return;
    this.dragDepth += 1;
    this.dropOverlay.classList.remove("hidden");
    this.dropOverlay.classList.add("flex");
  }

  handleDragLeave(event) {
    if (!this.eventHasFiles(event)) return;
    this.dragDepth -= 1;
    if (this.dragDepth <= 0) this.hideOverlay();
  }

  handleDrop(event) {
    event.preventDefault();
    this.dragDepth = 0;
    this.hideOverlay();
    if (!this.eventHasFiles(event)) return;
    this.enqueueFiles(Array.from(event.dataTransfer.files || []));
  }

  hideOverlay() {
    this.dropOverlay.classList.add("hidden");
    this.dropOverlay.classList.remove("flex");
  }

  eventHasFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes("Files");
  }

  // ---------- upload loop ----------

  async startUpload() {
    if (this.uploading || this.queue.length === 0) return;
    this.uploading = true;
    window.addEventListener("beforeunload", this.boundBeforeUnload);
    if (this.hasCloseDialogButtonTarget) this.closeDialogButtonTarget.classList.add("hidden");
    if (this.hasUploadDialogTarget && !this.uploadDialogTarget.open) {
      this.uploadDialogTarget.showModal();
    }
    this.showStatus();
    // Let the browser paint the initial "Nahrávam 1 z N…" status before the
    // first (CPU-blocking) checksum runs, so selecting many photos never looks
    // frozen.
    await this.yieldToBrowser();

    while (this.queue.length > 0) {
      const item = this.queue.shift();
      try {
        await this.uploadItem(item);
        this.done += 1;
      } catch {
        this.failed.push(item);
      }
      this.renderProgress(0);
      // Yield between files so the progress bar actually repaints instead of
      // jumping forward once the main thread is finally free.
      await this.yieldToBrowser();
    }

    this.onComplete();
  }

  // Hands control back to the event loop on a macrotask boundary, giving the
  // renderer a chance to paint queued DOM updates (progress bar / label).
  yieldToBrowser() {
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  uploadItem(item) {
    const upload = new DirectUpload(item.file, this.directUploadUrlValue);
    upload.delegate = {
      directUploadWillStoreFileWithXHR: (xhr) => {
        if (!xhr.upload) return;
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable && event.total > 0) this.renderProgress(event.loaded / event.total);
        });
      },
    };

    return new Promise((resolve, reject) => {
      upload.create(async (error, blob) => {
        if (error) return reject(error);
        try {
          const response = await post(this.urlValue, {
            body: { photo: { file: blob.signed_id }, ...this.contextParams() },
            responseKind: "turbo-stream",
          });
          if (!response.ok) throw new Error("Failed to create photo record.");
          resolve();
        } catch (e) {
          reject(e);
        }
      });
    });
  }

  contextParams() {
    if (!this.hasContextTarget) return {};
    try {
      return JSON.parse(this.contextTarget.dataset.params || "{}");
    } catch {
      return {};
    }
  }

  onComplete() {
    this.uploading = false;
    window.removeEventListener("beforeunload", this.boundBeforeUnload);
    if (this.hasCloseDialogButtonTarget) this.closeDialogButtonTarget.classList.remove("hidden");
    if (this.failed.length === 0) {
      // Nothing left to report: hide the progress UI (it lives inside a
      // dialog that may stay open, e.g. the onboarding picker) and close the
      // dedicated upload dialog where there is one.
      if (this.hasStatusContainerTarget) this.statusContainerTarget.classList.add("hidden");
      this.closeUploadDialog();
    } else {
      this.renderErrorSummary();
    }
  }

  retryFailed() {
    if (this.uploading || this.failed.length === 0) return;
    this.queue = this.failed;
    this.failed = [];
    this.done = 0;
    this.total = this.queue.length;
    this.startUpload();
  }

  // ---------- status / progress UI ----------

  showStatus() {
    if (this.hasStatusContainerTarget) this.statusContainerTarget.classList.remove("hidden");
    if (this.hasErrorSummaryTarget) this.errorSummaryTarget.classList.add("hidden");
    if (this.hasRetryButtonTarget) this.retryButtonTarget.classList.add("hidden");
    this.renderProgress(0);
  }

  // Progress is driven by completed-file count (plus the in-flight file's own
  // fraction) rather than total bytes, so a single large file can't make the
  // bar lurch. fileFraction is 0..1 for the file currently uploading.
  renderProgress(fileFraction = 0) {
    const completed = this.done + Math.min(fileFraction, 1);
    const pct = Math.min(100, Math.round((completed / (this.total || 1)) * 100));
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${pct}%`;
    if (this.hasProgressLabelTarget) {
      const current = Math.min(this.done + 1, this.total);
      this.progressLabelTarget.textContent = interpolate(this.messagesValue.uploading, { current, total: this.total, percent: pct });
    }
  }

  renderErrorSummary() {
    if (this.hasErrorSummaryTarget) {
      this.errorSummaryTarget.textContent = interpolate(this.messagesValue.failed, { done: this.done, failed: this.failed.length });
      this.errorSummaryTarget.classList.remove("hidden");
    }
    if (this.hasRetryButtonTarget) this.retryButtonTarget.classList.remove("hidden");
  }

  handleBeforeUnload(event) {
    if (this.uploading) event.preventDefault();
  }

  // ---------- library UI (counter / empty-state) ----------

  photoTargetConnected() {
    this.hideEmptyState();
    this.updateCounter();
  }

  photoTargetDisconnected() {
    this.updateEmptyState();
    this.updateCounter();
  }

  updateCounter() {
    const count = this.photoTargets.length;
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = this.maxValue > 0
        ? `${count} z ${this.maxValue} ${this.pluralizePhotos(this.maxValue)}`
        : `${count} ${this.pluralizePhotos(count)}`;
    }
    this.updateLimitState(count);
  }

  // With a max set: hide the upload button and show the notice once the
  // library is full; also enable/disable the continue link on emptiness.
  updateLimitState(count) {
    const full = this.maxValue > 0 && count >= this.maxValue;
    this.uploadButtonTargets.forEach((el) => el.classList.toggle("hidden", full));
    if (this.hasLimitNoticeTarget) this.limitNoticeTarget.classList.toggle("hidden", !full);
    if (this.hasContinueButtonTarget) {
      if (count === 0) this.continueButtonTarget.setAttribute("aria-disabled", "true");
      else this.continueButtonTarget.removeAttribute("aria-disabled");
    }
  }

  // Slovak and Czech share this rule: 1, then 2-4, then the rest. The three
  // forms come from the view so the wording follows the domain.
  pluralizePhotos(count) {
    const forms = this.photoNounValue;
    if (count === 1) return forms.one;
    if (count >= 2 && count <= 4) return forms.few;
    return forms.other;
  }

  updateEmptyState() {
    if (this.photoTargets.length === 0) this.showEmptyState();
    else this.hideEmptyState();
  }

  // The empty state and the grid may live inside a lazily loaded frame (the
  // onboarding picker dialog), so neither is guaranteed to exist on connect.
  showEmptyState() {
    if (!this.hasEmptyStateTarget || !this.hasPhotosContainerTarget) return;
    this.emptyStateTarget.classList.remove("hidden");
    this.emptyStateTarget.classList.add("flex");
    this.photosContainerTarget.classList.add("hidden");
    if (this.hasActionBarTarget) {
      this.actionBarTarget.classList.add("hidden");
      this.actionBarTarget.classList.remove("flex");
    }
  }

  hideEmptyState() {
    if (!this.hasEmptyStateTarget || !this.hasPhotosContainerTarget) return;
    this.emptyStateTarget.classList.add("hidden");
    this.emptyStateTarget.classList.remove("flex");
    this.photosContainerTarget.classList.remove("hidden");
    if (this.hasActionBarTarget) {
      this.actionBarTarget.classList.remove("hidden");
      this.actionBarTarget.classList.add("flex");
    }
  }
}
